// Running an operator HMI panel inside the app.
//
// The eight panels in `machine_hmi_interface/public` are self-contained pages —
// no stylesheet, no script, no image loaded from anywhere — served to the
// Siemens terminals by their own express app on :5005. "Show HMI Operator"
// opened that address in a new tab, which works on the factory network and
// nowhere else: :5005 is not on the tunnel, and over HTTPS a browser would
// refuse the plain-http address before even trying it.
//
// So the page is fetched as text and put into an iframe's `srcdoc`. A srcdoc
// iframe INHERITS THE PARENT'S ORIGIN, which is what makes this work rather
// than merely look like it works:
//
//   · localStorage is shared, so the token is already there
//   · no cross-origin frame, so nothing needs CORS or postMessage
//
// Two things are changed in the page, and nothing else:
//
//   1. the three API constants, which are hardcoded to `192.168.1.162` — the
//      same address problem the app itself had before config/api.js. They are
//      rewritten to whatever `API` resolved for THIS browser: per-port on the
//      LAN, empty string through the tunnel, where cloudflared routes by path.
//
//   2. an injected shim that attaches the token to every request. The panels
//      send one on exactly one call (assigning raw material) and none of the
//      others, because a Siemens terminal never has a token — its :5100 routes
//      are exempted by IP instead. That exemption is private-range only and
//      deliberately so, which means it is unavailable here, which in turn means
//      every call has to carry the user's token.

/** The three constants, exactly as they appear in all eight files. */
const CONSTANTS = [
  { name: 'API_BASE', line: 'var API_BASE = "http://192.168.1.162:5100";' },
  { name: 'STANDARD_API_BASE', line: 'var STANDARD_API_BASE = "http://192.168.1.162:5000";' },
  {
    name: 'RAW_MATERIAL_API',
    line: 'var RAW_MATERIAL_API = "http://192.168.1.162:5000/api/v1/raw-materials";',
  },
];

/**
 * Attach the logged-in user's token to everything the panel sends.
 *
 * Wrapping the two transports is deliberately preferred over editing eight
 * three-thousand-line files: the panels stay byte-identical to what the
 * terminals run, so a fix on the floor is a fix here with no second copy to
 * remember.
 *
 * Only same-origin and known-API requests are given the header — a panel that
 * one day fetches something public should not have a JWT posted to it.
 */
const authShim = () => `<script>
(function () {
  var BASES = __BASES__;
  var mine = function (url) {
    var u = String(url || '');
    if (u.indexOf('/api/') === 0) return true;
    for (var i = 0; i < BASES.length; i++) {
      if (BASES[i] && u.indexOf(BASES[i] + '/api/') === 0) return true;
    }
    return false;
  };
  var token = function () {
    try { return localStorage.getItem('token') || ''; } catch (e) { return ''; }
  };

  var open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__hmiAuth = mine(url);
    return open.apply(this, arguments);
  };
  var send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__hmiAuth) {
      var t = token();
      if (t) {
        try {
          this.setRequestHeader('x-auth-token', t);
          this.setRequestHeader('Authorization', 'Bearer ' + t);
        } catch (e) { /* headers already sent; the page sets its own */ }
      }
    }
    return send.apply(this, arguments);
  };

  if (window.fetch) {
    var f = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!mine(url)) return f.apply(this, arguments);
      var t = token();
      if (!t) return f.apply(this, arguments);
      var opts = init ? Object.assign({}, init) : {};
      var h = new Headers(opts.headers || (typeof input === 'object' && input.headers) || {});
      h.set('x-auth-token', t);
      h.set('Authorization', 'Bearer ' + t);
      opts.headers = h;
      return f.call(this, input, opts);
    };
  }
})();
</script>`;

/**
 * Rewrite a panel for the browser that is about to run it.
 *
 * @param html    the file, verbatim
 * @param bases   { redis, backend } — from config/api.js
 * @returns {{ html, rewritten: string[], missing: string[] }} — `missing` names
 *          any constant that was not found, so a panel edited on the floor
 *          cannot silently keep pointing at 192.168.1.162.
 */
export function prepareHmiPage(html, { redis = '', backend = '' } = {}) {
  const src = String(html || '');
  if (!src.trim()) return { html: '', rewritten: [], missing: CONSTANTS.map(c => c.name) };

  const replacements = {
    API_BASE: `var API_BASE = ${JSON.stringify(redis)};`,
    STANDARD_API_BASE: `var STANDARD_API_BASE = ${JSON.stringify(backend)};`,
    RAW_MATERIAL_API: `var RAW_MATERIAL_API = ${JSON.stringify(`${backend}/api/v1/raw-materials`)};`,
  };

  let out = src;
  const rewritten = [];
  const missing = [];
  for (const { name, line } of CONSTANTS) {
    if (out.includes(line)) {
      out = out.replace(line, replacements[name]);
      rewritten.push(name);
    } else {
      missing.push(name);
    }
  }

  // Before everything else the page runs, so the transports are already wrapped
  // when the panel makes its first poll.
  const shim = authShim().replace('__BASES__', JSON.stringify([redis, backend].filter(Boolean)));
  out = injectHead(out, shim);

  return { html: out, rewritten, missing };
}

/** Put the shim first inside <head>, or at the very top if there is no head. */
function injectHead(html, snippet) {
  const head = html.match(/<head[^>]*>/i);
  if (head) {
    const at = head.index + head[0].length;
    return html.slice(0, at) + '\n' + snippet + html.slice(at);
  }
  return snippet + '\n' + html;
}

/** Machines whose panel the app can open. Kept as data, not a hardcoded list. */
export const hmiPagePath = machineId =>
  `/api/redis/hmi/${encodeURIComponent(String(machineId || '').toLowerCase())}`;
